import { Observable, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from '../../util/log.service';
import { API_ENDPOINTS, ApiService } from '../api.service';
import { GenericApiClientService } from '../generic-api-client.service';
import { IPaymentTransactionModel } from '../payment-transaction/payment-transaction-model.interface';
import { IParkingRequestModel } from './parking-request-model.interface';
import { ParkingRequestTransformService } from './parking-request-transform.service';

/**
 * Client service for handling parking request-related API operations
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRequestClientService extends GenericApiClientService<IParkingRequestModel> {
  /**
   * Constructor for ParkingRequestClientService
   * @param http HttpClient for making HTTP requests
   * @param apiService ApiService for API related utilities
   * @param log LogService for logging
   */
  constructor(http: HttpClient, apiService: ApiService, log: LogService, transformer: ParkingRequestTransformService) {
    super(http, apiService, log, transformer);
    this.itemsUrl = apiService.getUrlFor(API_ENDPOINTS.parkingRequests);
    this.singleItemUrl = apiService.getUrlFor(API_ENDPOINTS.parkingRequest);
  }

  /**
   * Override createItem to handle backend limitation with start_date and end_date
   * Performs a two-step process: create without dates, then update with dates
   * This workaround is invisible to UI services
   * @param item The parking request payload from buildFullPayload method
   * @returns Observable of the created and updated parking request
   */
  generatePaymentTransaction(parkingRequestId: number): Observable<IPaymentTransactionModel> {
    const url = `${this.itemsUrl}/${parkingRequestId}/generatepayment`;
    this.log.debug('Generating payment transaction for parking request', parkingRequestId);
    return this.http.post<IPaymentTransactionModel>(url, {});
  }

  override createItem(item: any): Observable<IParkingRequestModel> {
    this.log.debug('Creating parking request with create-patch workaround', item);

    // Separate date fields from the main payload
    const { start_date, end_date, ...createPayload } = item;
    const hasDateFields = start_date || end_date;

    // Step 1: Create the parking request without date fields
    const createObservable = super.createItem(createPayload);

    // If no date fields, just return the create result
    if (!hasDateFields) {
      this.log.debug('No date fields to update, returning create result');
      return createObservable;
    }

    // Step 2: Update with date fields after creation
    return createObservable.pipe(
      switchMap((createdItem: IParkingRequestModel) => {
        this.log.debug('Created parking request, now updating with date fields', {
          id: createdItem.id,
          start_date,
          end_date
        });

        // Build update payload with only date fields
        const updatePayload: Partial<IParkingRequestModel> = {
          id: createdItem.id
        };

        if (start_date) {
          updatePayload.start_date = start_date;
        }

        if (end_date) {
          updatePayload.end_date = end_date;
        }

        // Perform the update
        return super.updateItem(updatePayload as IParkingRequestModel);
      })
    );
  }
}
